import { useQuery } from '@tanstack/react-query'
import { getCustomBaseChain } from 'customChains'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { BASE_MAINNET, useConnectivity } from 'use-towns-client'
import { Address, createPublicClient, erc20Abi, http } from 'viem'

const QUERY_KEY = 'contractHasTransferMethod'

// // Minimal ABI to check for transfer method
// const transferAbi = [
//     {
//         inputs: [
//             { name: 'to', type: 'address' },
//             { name: 'amount', type: 'uint256' },
//         ],
//         name: 'transfer',
//         outputs: [{ name: '', type: 'bool' }],
//         stateMutability: 'nonpayable',
//         type: 'function',
//     },
// ] as const

const baseChain = getCustomBaseChain(BASE_MAINNET)
const publicClient = createPublicClient({
    chain: baseChain,
    transport: http(baseChain.rpcUrls.default.http[0]),
})

export function useContractHasTransferMethod(args: {
    contractAddress: Address | undefined
    enabled: boolean
}) {
    const { contractAddress, enabled } = args
    const { data: myAbstractAccountAddress } = useMyAbstractAccountAddress()
    const { loggedInWalletAddress } = useConnectivity()
    return useQuery({
        queryKey: [QUERY_KEY, contractAddress],
        queryFn: async () => {
            if (!contractAddress || !myAbstractAccountAddress || !loggedInWalletAddress) {
                return false
            }

            try {
                // Get the contract bytecode to check if it's a contract
                const bytecode = await publicClient.getCode({ address: contractAddress })
                if (!bytecode || bytecode === '0x') {
                    return false
                }

                // Try to simulate calling the transfer function
                // This will throw if the method doesn't exist
                await publicClient.simulateContract({
                    address: contractAddress,
                    abi: erc20Abi,
                    functionName: 'transfer',
                    args: [loggedInWalletAddress, 10n], // dummy args
                    account: myAbstractAccountAddress,
                    stateOverride: [
                        {
                            address: loggedInWalletAddress,
                            balance: 1000000000000000000n,
                        },
                    ],
                })

                // If we reach here, the contract has a transfer method
                return true
            } catch (error) {
                // Contract doesn't have transfer method or call reverted
                console.error(`useContractHasTransferMethod error: ${error}`)
                return false
            }
        },
        enabled: !!contractAddress && !!myAbstractAccountAddress && enabled,
        // Cache forever - contracts don't change their interface
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    })
}
